/**
 * Générateur — Automatisme : unités de longueur successives
 * Attendu ID : 1021 | Niveau : 6ème | Domaine : GM
 * Rubrique : AUTOMATISME (programme 2025) — « L'élève connaît les relations
 * entre deux unités successives du système décimal, par exemple 1 dm = 10 cm
 * et 1 cm = 1/10 dm = 0,1 dm. »
 * Labels (cartes exerciseur) :
 *   - Unités successives (1 dm = 10 cm)
 *
 * ── Plan de cartes (2026-08-21, v2 après retour prof) ──────────────────────
 * PIVOT DIDACTIQUE (prof) : la réponse numérique était toujours 10 ou 0,1 —
 * réussite possible à pile ou face (A2). Le savoir réel, c'est QUELLE UNITÉ
 * est la voisine : partout, le nombre est DONNÉ et L'UNITÉ EST LA RÉPONSE.
 * DOMAINE FERMÉ : les 6 paires VOISINES de la chaîne km-hm-dam-m-dm-cm-mm,
 * deux sens (12 faits) : descendant « 1 dm = 10 cm », montant « 1 cm =
 * 1/10 dm = 0,1 dm » (la double écriture = la nouveauté 6e, format dédié).
 * V/F : les nombres restent dans {10 ; 0,1 ; 1/10}, ce sont les UNITÉS qui
 * rendent la ligne vraie ou fausse (arbitrage prof).
 * INTERDITS : aucune paire NON contiguë en réponse juste (1 cm = ... m
 * relève du gen_1020 — mais une unité non contiguë PEUT servir de piège),
 * aucune conversion de valeur quelconque, aucun tableau de conversion.
 * L'adjacence de chaque fait émis est GARDÉE (throw).
 */
import {
    randomChoice, randInt, shuffle,
    makeQcm, makeVraiFaux, makeTexteTrou, makeRelier,
} from '../../../utils';

const ATTENDU_ID = 1021;
const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const L1 = 'Unités successives (1 dm = 10 cm)';

// ─── La chaîne des unités (source de vérité unique) ───────────────────────

const CHAINE = ['km', 'hm', 'dam', 'm', 'dm', 'cm', 'mm'];
const IDX = Object.fromEntries(CHAINE.map((u, i) => [u, i]));

const garde = (condition, message) => {
    if (!condition) throw new Error(`gen_1021 : ${message}`);
};

/**
 * Un FAIT : la paire voisine (i, i + 1) lue dans un sens. La RÉPONSE de tous
 * les formats est l'unité de droite. L'adjacence est GARDÉE (throw).
 */
const fait = (i, sens) => {
    garde(i >= 0 && i + 1 < CHAINE.length, `paire (${i}) hors chaîne`);
    const grande = CHAINE[i];
    const petite = CHAINE[i + 1];
    garde(Math.abs(IDX[grande] - IDX[petite]) === 1, 'paire non contiguë');
    return sens === 'descendant'
        ? { gauche: grande, droite: petite, valeur: '10', sens, cle: `${grande}>${petite}` }
        : { gauche: petite, droite: grande, valeur: '0,1', sens, cle: `${petite}<${grande}` };
};

const tirerFaits = (nb) => {
    const tous = [];
    for (let i = 0; i < CHAINE.length - 1; i++) {
        tous.push(fait(i, 'descendant'));
        tous.push(fait(i, 'montant'));
    }
    // tiroirs : au moins un fait de chaque sens
    for (let essai = 0; essai < 30; essai++) {
        const tirage = shuffle(tous).slice(0, nb);
        const descendants = tirage.filter((f) => f.sens === 'descendant');
        if (descendants.length >= 1 && descendants.length < tirage.length) return tirage;
    }
    return shuffle(tous).slice(0, nb);
};

// ─── Format 1 : l'unité à trous (saisie, une égalité par ligne) ───────────

const genRelationsTrouSaisie = () => {
    const faits = tirerFaits(4);
    // le nombre est DONNÉ, l'élève écrit l'unité voisine
    const phrase = faits.map((f) => `1 ${f.gauche} = ${f.valeur} {{${f.droite}}}`).join('\n');
    return makeTexteTrou(
        phrase,
        '<p><b>Complète chaque égalité avec l\'unité qui convient.</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 2 : la double écriture fraction puis décimal (saisie) ─────────

const genFractionTrouSaisie = () => {
    // sens MONTANT uniquement : « 1 cm = 1/10 {{dm}} = 0,1 {{dm}} » — le
    // dénominateur est AFFICHÉ, les deux trous attendent la MÊME unité.
    // Ligne ENTIÈREMENT en math (piège vécu 1011 : le mixte texte + math
    // n'est pas rendu) ; les trous restent HORS du \text (le moteur remplace
    // les trous par un placeholder avant de rendre KaTeX).
    const indices = shuffle([0, 1, 2, 3, 4, 5]).slice(0, randomChoice([2, 3]));
    const lignes = indices.map((i) => {
        const petite = CHAINE[i + 1];
        const grande = CHAINE[i];
        return `$1\\ \\text{${petite}} = \\dfrac{1}{10}\\ {{${grande}}} = 0{,}1\\ {{${grande}}}$`;
    });
    return makeTexteTrou(
        lignes.join('\n'),
        '<p><b>Complète avec l\'unité qui convient (fraction, puis écriture décimale).</b></p>',
        { ...opts(L1), mode: 'saisie' },
    );
};

// ─── Format 3 : QCM d'unités à erreurs nommées ────────────────────────────

const genRelationsQcm = () => {
    for (let essai = 0; essai < 40; essai++) {
        const i = randInt(0, 5);
        const sens = randomChoice(['descendant', 'montant']);
        const f = fait(i, sens);
        // Forme RETOURNÉE ~20 % (demande prof 2026-08-21) : « 10 cm = 1 ... »
        // au lieu de « 1 dm = 10 ... » — même fait, lecture dans l'autre sens.
        // base = l'unité AFFICHÉE, bonne = l'unité cachée ; les pièges se
        // calculent autour de la base, quel que soit le sens d'affichage.
        const retournee = Math.random() < 0.2;
        const base = retournee ? f.droite : f.gauche;
        const bonne = retournee ? f.gauche : f.droite;
        const ig = IDX[base];
        const dirVoisine = IDX[bonne] - ig;               // +1 ou −1
        const candidats = [];
        // la voisine du MAUVAIS côté (1 dm = 10 m ?)
        if (CHAINE[ig - dirVoisine] !== undefined) {
            candidats.push({ valeur: CHAINE[ig - dirVoisine], code: 'voisine_du_mauvais_cote' });
        }
        // deux rangs au lieu d'un (1 dm = 10 mm ?) — piège non contigu
        if (CHAINE[ig + 2 * dirVoisine] !== undefined) {
            candidats.push({ valeur: CHAINE[ig + 2 * dirVoisine], code: 'deux_rangs' });
        }
        // la même unité recopiée (1 dm = 10 dm ?)
        candidats.push({ valeur: base, code: 'meme_unite' });
        // en repli : deux rangs du mauvais côté
        if (CHAINE[ig - 2 * dirVoisine] !== undefined) {
            candidats.push({ valeur: CHAINE[ig - 2 * dirVoisine], code: 'deux_rangs' });
        }
        // mélange AVANT sélection : chaque fait a jusqu'à 4 pièges valides,
        // le sous-ensemble tiré varie (sinon la grille serait déterministe).
        // Bouts de chaîne (km, mm) : seulement 2 pièges existent → grille de
        // 3 choix acceptée (décision prof 2026-08-21), jamais moins.
        const vus = new Set([bonne]);
        const dis = shuffle(candidats).filter((c) => {
            if (vus.has(c.valeur)) return false;
            vus.add(c.valeur);
            return true;
        }).slice(0, 3);
        if (dis.length < 2) continue;
        const texte = retournee
            ? `<p><b>Complète : ${f.valeur} ${f.droite} = 1 ...</b></p>`
            : `<p><b>Complète : 1 ${f.gauche} = ${f.valeur} ...</b></p>`;
        const payload = makeQcm(
            [bonne, ...dis.map((c) => c.valeur)],
            bonne,
            texte,
            opts(L1),
            false,
        );
        payload._meta.params = { fait: f.cle, forme: retournee ? 'retournee' : 'directe', distracteurs: dis };
        return payload;
    }
    const payload = makeQcm(['cm', 'm', 'mm', 'dm'], 'cm',
        '<p><b>Complète : 1 dm = 10 ...</b></p>', opts(L1), false);
    payload._meta.params = { fait: 'dm>cm', distracteurs: [] };
    return payload;
};

// ─── Format 4 : tableau Vrai/Faux (les unités portent le vrai/faux) ───────

/** Vérité d'une ligne « 1 a = v b » : voisines ET nombre dans le bon sens. */
const verite = (a, v, b) => {
    if (IDX[a] === undefined || IDX[b] === undefined) return false;
    if (Math.abs(IDX[a] - IDX[b]) !== 1) return false;
    return v === '10' ? IDX[a] < IDX[b] : IDX[a] > IDX[b];
};

const genRelationsVraiFaux = () => {
    for (let essai = 0; essai < 40; essai++) {
        const faits = tirerFaits(4);
        const lignes = [];
        const verites = [];
        for (const f of faits) {
            const enFraction = f.sens === 'montant' && Math.random() < 0.35;
            const ig = IDX[f.gauche];
            const dirVoisine = IDX[f.droite] - ig;
            // l'unité affichée : la bonne, ou un piège NOMMÉ (le nombre ne
            // change jamais : {10 ; 0,1 ; 1/10}, arbitrage prof 2026-08-21)
            let uniteAffichee;
            if (Math.random() < 0.5) uniteAffichee = f.droite;
            else {
                const pieges = [];
                if (CHAINE[ig - dirVoisine] !== undefined) pieges.push(CHAINE[ig - dirVoisine]);   // mauvais côté
                if (CHAINE[ig + 2 * dirVoisine] !== undefined) pieges.push(CHAINE[ig + 2 * dirVoisine]); // deux rangs
                uniteAffichee = randomChoice(pieges);
            }
            // Vérité RE-CALCULÉE depuis l'affiché (jamais l'intention) :
            const vNum = enFraction ? '0,1' : f.valeur;
            const estVraie = verite(f.gauche, vNum, uniteAffichee);
            lignes.push(enFraction
                ? `1 ${f.gauche} = $\\dfrac{1}{10}$ ${uniteAffichee}`
                : `1 ${f.gauche} = ${f.valeur} ${uniteAffichee}`);
            verites.push(estVraie);
        }
        const nbVraies = verites.filter(Boolean).length;
        if (nbVraies < 1 || nbVraies > 3) continue;
        return makeVraiFaux(
            lignes,
            verites.map((v) => (v ? 0 : 1)),
            '<p><b>Vrai ou faux ?</b></p>',
            opts(L1),
        );
    }
    return makeVraiFaux(
        ['1 dm = 10 cm', '1 dm = 10 m', '1 m = 10 dm', '1 mm = 0,1 cm'],
        [0, 1, 0, 0],
        '<p><b>Vrai ou faux ?</b></p>',
        opts(L1),
    );
};

// ─── Format 5 : relier chaque longueur à son écriture voisine ─────────────

const genPairesRelier = () => {
    for (let essai = 0; essai < 40; essai++) {
        // tout le tirage dans le MÊME sens : « 1 dm » n'a alors qu'une seule
        // cible juste (1 dm = 10 cm ET 0,1 m seraient ambigus sinon)
        const sens = randomChoice(['descendant', 'montant']);
        const indices = shuffle([0, 1, 2, 3, 4, 5]).slice(0, randomChoice([3, 4]));
        const pairs = indices.map((i) => {
            const f = fait(i, sens);
            return { source: `1 ${f.gauche}`, target: `${f.valeur} ${f.droite}` };
        });
        // leurres : même nombre, unité NON voisine d'aucune source (nommés)
        const cibles = new Set(pairs.map((p) => p.target));
        const leurres = [];
        for (const u of shuffle([...CHAINE])) {
            if (leurres.length >= 2) break;
            const valeur = sens === 'descendant' ? '10' : '0,1';
            const cand = `${valeur} ${u}`;
            if (cibles.has(cand)) continue;
            // le leurre ne doit être juste pour AUCUNE source du tirage
            const justePourUne = pairs.some((p) => verite(p.source.slice(2), valeur, u));
            if (justePourUne) continue;
            leurres.push({ target: cand, code: 'unite_non_voisine' });
        }
        if (leurres.length < 1) continue;
        const payload = makeRelier(
            pairs,
            '<p><b>Relie chaque longueur à son écriture dans l\'unité voisine.</b></p>',
            { ...opts(L1), cibles_leurres: leurres.slice(0, randomChoice([1, 2])) },
        );
        payload._meta.params = { sens, leurres };
        return payload;
    }
    return makeRelier(
        [{ source: '1 dm', target: '10 cm' }, { source: '1 cm', target: '10 mm' },
            { source: '1 m', target: '10 dm' }],
        '<p><b>Relie chaque longueur à son écriture dans l\'unité voisine.</b></p>',
        opts(L1),
    );
};

// ─── Contrat distracteurs (référence de revue) ────────────────────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'PARTOUT le nombre est donné ({10 ; 0,1 ; 1/10}) et L\'UNITÉ est la réponse (pivot prof 2026-08-21 : la réponse numérique à pile ou face était un défaut A2)',
        'domaine FERMÉ : les 6 paires VOISINES de km-hm-dam-m-dm-cm-mm, deux sens (12 faits) ; adjacence gardée par un throw',
        'jamais de conversion de valeur quelconque, jamais de tableau de conversion',
    ],
    successives_qcm_unique: {
        support: '« 1 dm = 10 ... » : les choix sont des UNITÉS',
        fixes: {
            voisine_du_mauvais_cote: 'la voisine de l\'autre côté (1 dm = 10 m)',
            deux_rangs: 'deux rangs au lieu d\'un (1 dm = 10 mm) — le piège non contigu',
            meme_unite: 'l\'unité recopiée telle quelle (1 dm = 10 dm)',
        },
    },
    successives_relier: {
        support: '3 ou 4 paires du MÊME sens (sinon « 1 dm » aurait deux cibles justes), cibles = nombre + unité',
        fixes: { unite_non_voisine: 'même nombre, unité qui n\'est voisine d\'aucune source du tirage' },
    },
};

// ─── Registre et point d'entrée ───────────────────────────────────────────

const GENERATORS = {
    successives_texte_trou_saisie: genRelationsTrouSaisie,
    successives_fraction_trou_saisie: genFractionTrouSaisie,
    successives_qcm_unique: genRelationsQcm,
    successives_tableau_vf: genRelationsVraiFaux,
    successives_relier: genPairesRelier,
};

const FORMAT_LABELS = {
    successives_texte_trou_saisie: 'Saisie',
    successives_fraction_trou_saisie: 'Fractions',
    successives_qcm_unique: 'QCM',
    successives_tableau_vf: 'Vrai/Faux',
    successives_relier: 'Relier',
};

export const CATEGORIES = [
    {
        id: 'successives',
        label: L1,
        formatsList: Object.keys(GENERATORS).map((k) => ({ key: k, label: FORMAT_LABELS[k] || k })),
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
